import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { useQuery, useMutation } from '@apollo/client';

import Tag from '../Tag';
import NewAddress from './NewAddress';
import ModalV2 from 'core/ui/components/V2/ModalV2';
import { ReactComponent as StarFilledIcon } from 'core/assets/images/V2/icons/StarFilledIcon.svg';
import { ReactComponent as StarIcon } from 'core/assets/images/V2/icons/StarIcon.svg';
import { Button, Box, Flex, Hr, Text, Radio } from 'core/ui/atoms';
import Loading from 'core/ui/components/Loading';
import { isLoading } from 'core/api/loadingHelpers';
import { LIST_USER_ADDRESSES } from 'core/api/apollo/queries';
import { DELETE_OR_SET_PRIMARY_ADDRESS, SET_DELIVERY_INFO } from 'core/api/apollo/mutations';
import useUser from 'core/hooks/useUser';
import InfoModal from 'core/ui/components/V2/InfoModal';

const AddressListModal = ({ closeModal }) => {
  const [infoMessage, setInfoMessage] = useState(null);
  const { currentUser, refetchUser } = useUser();
  const { loading, error, data: { listUserAddresses = [] } = {}, refetch } = useQuery(LIST_USER_ADDRESSES);

  const [selectedAddress, setSelectedAddress] = useState(null);
  const [newAddress, setNewAddress] = useState(false);

  useEffect(() => {
    if (listUserAddresses && listUserAddresses.length > 0) {
      const primaryAddress = listUserAddresses.find((addr) => addr.primary);
      if (primaryAddress) {
        setSelectedAddress(primaryAddress);
      }
    }
  }, [listUserAddresses]);

  const [deleteOrSetPrimaryAddress] = useMutation(DELETE_OR_SET_PRIMARY_ADDRESS, {
    onCompleted: async (data) => {
      // eslint-disable-next-line no-console
      console.log('primary address updated successfully:', data);
      await refetch();
      await refetchUser();
    },
    onError: (error) => {
      // eslint-disable-next-line no-console
      console.error('Error updating primary address:', error);
    },
  });

  const renderErrorMessage = (status) => {
    if (status === '403') {
      setInfoMessage("Your delivery address can't be automatically changed as programs and prices vary based on zip codes. If this change is absolutely needed, please email help@gomethodology.com for assistance. Note that a new charge may be processed on your card for this change depending on your new delivery zip code.");
    } else if (status === '400') {
      setInfoMessage("Your delivery address can't be changed as programs vary based on zip codes. To change to this zip code permanently, you'll need to create a new account or email help@gomethodology.com for assistance");
    }
  };

  const [setDeliveryInfo] = useMutation(SET_DELIVERY_INFO, {
    onCompleted: async (data) => {
      // eslint-disable-next-line no-console
      console.log('Delivery info and SMS preferences updated successfully:', data);
      renderErrorMessage(data?.setDeliveryInfo?.errors[0]?.message);
      await refetch();
      await refetchUser();
      setNewAddress(false);
    },
    onError: (error) => {
      // eslint-disable-next-line no-console
      console.error('Error updating delivery info and SMS preferences:', error);
    },
  });

  if (isLoading(loading, listUserAddresses, error)) {
    return <Loading />;
  }

  const isSelectionChanged = () => {
    const primaryAddress = listUserAddresses.find((addr) => addr.primary);
    return primaryAddress.primary !== selectedAddress?.primary;
  };

  const handleDeleteAddress = async (address) => {
    try {
      await deleteOrSetPrimaryAddress({
        variables: {
          input: {
            addressId: address?.id,
            delete: true,
          },
        },
      });
    } catch (error) {
      console.error('Failed to update quantity:', error);
    }
  };

  const handlePrimaryAddress = async () => {
    try {
      await deleteOrSetPrimaryAddress({
        variables: {
          input: {
            addressId: selectedAddress?.id,
            delete: false,
          },
        },
      });
    } catch (error) {
      console.error('Failed to update quantity:', error);
    }
  };

  const handleNewAddress = async (addressParams) => {
    try {
      await setDeliveryInfo({
        variables: {
          input: {
            street1: addressParams?.street1,
            street2: addressParams?.street2,
            city: addressParams?.city,
            instructions: addressParams?.instructions,
            zipcode: addressParams?.zipcode,
          },
        },
      });
    } catch (error) {
      console.error('Failed to create new address:', error);
    }
  };

  // eslint-disable-next-line no-console
  console.log(selectedAddress);

  const AddressItem = () => {
    return listUserAddresses.map((address, index) => {
      return (
        <Container
          direction='row'
          alignItems='center'
          justifyContent='center'
          key={index}
          $selected={selectedAddress?.id === address?.id}
        >
          <Radio
            value="0"
            name=""
            checked={selectedAddress?.id === address?.id}
            onChange={() => setSelectedAddress(address)}
          />
          <AddressContainer>
            <Flex direction='row' alignItems='center' justifyContent='space-between'>
              <Flex direction='row' alignItems='center' justifyContent='center' gap='10px'>
                <AddressText>
                  {`${currentUser?.firstName} ${currentUser?.lastName}`}
                </AddressText>
                {address?.primary ? <StarFilledIcon /> : <StarIcon />}
                {address?.primary && <Tag text='Default' />}
              </Flex>
              {!address?.primary &&
                <DeleteTextUnderLine onClick={() => handleDeleteAddress(address)}>
                  DELETE
                </DeleteTextUnderLine>
              }
            </Flex>
            <AddressText>{address?.street1}{' '}{address?.street2}</AddressText>
            <AddressText>{`${address?.city}, ${address?.state}, ${address?.zipCode} US`}</AddressText>
          </AddressContainer>
        </Container>
      );
    });
  };

  const RenderAddressList = () => (
    <>
      <ModalTitle>My Addresses</ModalTitle>
      <Hr />
      <AddressItem />
      <TextUnderLine onClick={() => setNewAddress(true)}>ADD NEW ADDRESS</TextUnderLine>
      <Description onClick={() => setNewAddress(true)}>
        Address changes will apply to your next unlocked delivery.
        If you need an address change to take effect immediately, please email us at help@gomethodology.com.
      </Description>
      {isSelectionChanged() &&
        <StyledPaymentButton onClick={handlePrimaryAddress}>
          CONFIRM
        </StyledPaymentButton>
      }
    </>
  );

  return (
    <>
      <ModalV2 data-account-info-modal onClose={closeModal}>
        {newAddress ?
          <NewAddress
            handleNewAddress={handleNewAddress}
            goBack={setNewAddress}
          /> :
          <RenderAddressList />
        }
      </ModalV2>
      {infoMessage && <InfoModal
        title='Cannot Change Address'
        description={infoMessage}
        ctaText='OK'
        onCtaClick={() => setInfoMessage(null)}
        onClose={() => setInfoMessage(null)}
        />
      }
    </>
  );
};

AddressListModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
};

const StyledPaymentButton = styled(Button)`
  padding: 12px 10px;
  border-radius: 4px;
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
  gap: 10px;
  margin-top: 50px;
  background: ${(p) => p.theme.colors.green.light};
  ${(p) => p.theme.max('sm')`
    margin-bottom: 0;
  `}
`;

const Description = styled(Text)`
  font-family: ${({ theme }) => theme.typography.familyPoppins};
  color: ${({ theme }) => theme.colors.darkGreen};
  text-align: center;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  letter-spacing: 1.2px;
  margin: 0px;
  margin-top: 25px;
`;

const TextUnderLine = styled(Text)`
  font-family: ${({ theme }) => theme.typography.familyPoppins};
  color: ${({ theme }) => theme.colors.darkGreen};
  text-align: center;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  text-decoration: underline;
  margin: 0px;
  text-underline-offset: 5px;
  margin-top: 50px;
`;

const DeleteTextUnderLine = styled(Text)`
  font-family: ${({ theme }) => theme.typography.familyPoppins};
  color: ${({ theme }) => theme.colors.darkGreen};
  text-align: center;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  text-decoration: underline;
  margin: 0px;
  text-underline-offset: 5px;
`;

const Container = styled(Flex)`
  width: 100%;
  border-radius: 4px;
  margin-top: 20px;
  background: ${({ $selected }) => $selected ? 'rgba(197, 209, 192, 0.24)' : 'none'};
`;

const AddressContainer = styled(Box)`
  width: 90%;
  padding-left: 10px;
`;

const AddressText = styled(Text)`
  font-family: ${({ theme }) => theme.typography.familyPoppins};
  font-weight: 400;
  letter-spacing: 0%;
  margin: 0px;
  margin-top: 2px;
  margin-bottom: 2px;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 28px;
`;

const ModalTitle = styled(Text)`
  text-align: center;
  font-size: 30px;
  font-style: normal;
  font-weight: 400;
  line-height: 32px;
  letter-spacing: 0.9px;
  padding-top: 100px;
  margin: 0px;
  font-family: ${({ theme }) => theme.typography.familyLarken};
  color: ${({ theme }) => theme.colors.darkGreen};
`;

export default AddressListModal;
